import { Input, type InputProps } from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";

interface DebouncedInputProps extends Omit<InputProps, "value" | "onChange"> {
    value: string;
    onChange: (value: string) => void;
    debounceMs?: number;
}

/**
 * A controlled Input that shows keystrokes immediately but debounces
 * the `onChange` callback so upstream filter state only updates after
 * the user pauses typing.
 */
export function DebouncedInput({ value, onChange, debounceMs = 350, ...rest }: DebouncedInputProps) {
    const [local, setLocal] = useState(value);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Sync if the external value changes (e.g. "Clear All" resets to "")
    useEffect(() => {
        setLocal(value);
    }, [value]);

    function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
        const next = e.target.value;
        setLocal(next);
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => onChange(next), debounceMs);
    }

    // Flush on unmount to avoid a stale timer firing after component removal
    useEffect(
        () => () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        },
        []
    );

    return <Input {...rest} value={local} onChange={handleChange} />;
}
